import { ChatType } from 'im.v2.const';
import { UserManager } from 'im.v2.lib.user';

import {
	type RawChat,
	type RawFile,
	type RawUser,
	type RawShortUser,
	type RawMessage,
	type RawCommentInfo,
	type RawCollabInfo,
	type RawPin,
	type RawReaction,
	type RawCopilot,
	type RawMessagesAutoDeleteConfig,
	type RawStickerMessage,
} from 'im.v2.provider.service.types';

import { type ChatLoadRestResult, type RawSticker } from '../types/chat';

export class ChatDataExtractor
{
	#restResult: ChatLoadRestResult;

	constructor(restResult: ChatLoadRestResult)
	{
		this.#restResult = restResult;
	}

	getChatId(): number
	{
		return this.#restResult.chat.id;
	}

	getDialogId(): string
	{
		return this.#restResult.chat.dialogId;
	}

	isOpenlinesChat(): boolean
	{
		return this.#restResult.chat.type === ChatType.lines;
	}

	isCopilotChat(): boolean
	{
		return this.#restResult.chat.type === ChatType.copilot;
	}

	isCollabChat(): boolean
	{
		return this.#restResult.chat.type === ChatType.collab;
	}

	getParentChat(): ?RawChat
	{
		return this.#restResult.parentChat;
	}

	getChats(): RawChat[]
	{
		const mainChat = {
			...this.#restResult.chat,
			hasPrevPage: this.#restResult.hasPrevPage,
			hasNextPage: this.#restResult.hasNextPage,
			tariffRestrictions: this.#restResult.tariffRestrictions,
		};

		const chats = {
			[this.#restResult.chat.dialogId]: mainChat,
		};

		this.#restResult.users.forEach((user) => {
			if (chats[user.id])
			{
				chats[user.id] = { ...chats[user.id], ...UserManager.getDialogForUser(user) };
			}
			else
			{
				chats[user.id] = UserManager.getDialogForUser(user);
			}
		});

		const parentChat = this.getParentChat();
		if (parentChat)
		{
			chats[parentChat.dialogId] = parentChat;
		}

		return Object.values(chats);
	}

	getFiles(): RawFile[]
	{
		return this.#restResult.files ?? [];
	}

	getUsers(): RawUser[]
	{
		return this.#restResult.users ?? [];
	}

	getAdditionalUsers(): RawShortUser[]
	{
		return this.#restResult.usersShort ?? [];
	}

	getMessages(): RawMessage[]
	{
		return this.#restResult.messages ?? [];
	}

	getCommentInfo(): RawCommentInfo[]
	{
		return this.#restResult.commentInfo ?? [];
	}

	getStickerMessages(): RawStickerMessage[]
	{
		const stickerMessages = [];
		if (!this.#restResult.messages)
		{
			return stickerMessages;
		}

		this.#restResult.messages.forEach((message) => {
			const isSticker = Boolean(message.params.STICKER_PARAMS);
			if (!isSticker)
			{
				return;
			}

			stickerMessages.push({
				messageId: message.id,
				...message.params.STICKER_PARAMS,
			});
		});

		return stickerMessages;
	}

	getStickers(): RawSticker[]
	{
		return this.#restResult.stickers ?? [];
	}

	getCollabInfo(): ?RawCollabInfo
	{
		return this.#restResult.collabInfo ?? null;
	}

	getMessagesToStore(): RawMessage[]
	{
		return this.#restResult.additionalMessages ?? [];
	}

	getPinnedMessageIds(): number[]
	{
		const pinnedMessageIds = [];
		const pins: RawPin[] = this.#restResult.pins ?? [];
		pins.forEach((pin) => {
			pinnedMessageIds.push(pin.messageId);
		});

		return pinnedMessageIds;
	}

	getReactions(): RawReaction[]
	{
		return this.#restResult.reactions ?? [];
	}

	getCopilot(): RawCopilot
	{
		return this.#restResult.copilot;
	}

	getAutoDeleteConfig(): RawMessagesAutoDeleteConfig[]
	{
		return this.#restResult.messagesAutoDeleteConfigs;
	}
}
